// src/app/features/auth/reducers/program.reducer.ts
import { createReducer, on } from '@ngrx/store';
import {
  IProgram,
  IProgramListFilter,
  IProgramListSort,
  IProgramName,
} from '../../models/program.model';
import { ProgramActions } from './program.actions';
import { AuthActions } from '../auth/auth.actions';

export interface ProgramState {
  program: IProgram | null; // Allow both IProgram and null
  saveSuccess: boolean;
  saveFailure: boolean;
  programs: IProgram[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  filter: IProgramListFilter;
  sort: IProgramListSort;
  error: string | null;
  programNames: IProgramName[];
}

export const initialProgramState: ProgramState = {
  program: null, // Add this
  programs: [],
  programNames: [],
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  filter: {},
  sort: {
    key: 'createdAt',
    order: 'desc',
  },
  error: null,
  saveSuccess: false,
  saveFailure: false,
};

export const programReducer = createReducer(
  initialProgramState,
  // Load Programs
  on(ProgramActions.listPrograms, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ProgramActions.listProgramsSuccess, (state, { response, filters }) => ({
    ...state,
    programs: response.data.programs.list,
    total: response.data.programs.total,
    page: response.data.programs.page,
    limit: response.data.programs.limit,
    filter: filters.body?.filter ?? state.filter,
    loading: false,
    error: null,
  })),
  on(ProgramActions.listProgramsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(ProgramActions.listProgramNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ProgramActions.listProgramNamesSuccess, (state, { response }) => ({
    ...state,
    programNames: response.data.programs,
    loading: false,
    error: null,
  })),
  on(ProgramActions.listProgramNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(ProgramActions.listProgramNamesByClientIds, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ProgramActions.listProgramNamesByClientIdsSuccess, (state, { response }) => ({
    ...state,
    programNames: response.data.programs,
    loading: false,
    error: null,
  })),
  on(ProgramActions.listProgramNamesByClientIdsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  //disposition module usecase
  on(ProgramActions.getProgramName_Client, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ProgramActions.getProgramName_ClientSuccess, (state, { response }) => ({
    ...state,
    programNames: response.data.programs,
    loading: false,
    error: null,
  })),
  on(ProgramActions.getProgramName_ClientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  // For single program
  on(ProgramActions.loadSingleProgram, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ProgramActions.loadSingleProgramSuccess, (state, { program }) => ({
    ...state,
    program: program,
    loading: false,
    error: null,
  })),
  on(ProgramActions.loadSingleProgramFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Create Program
  on(ProgramActions.createProgram, (state) => ({
    ...state,
    loading: true,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(ProgramActions.createProgramSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    saveSuccess: true,
  })),
  on(ProgramActions.createProgramFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    saveFailure: true,
    program:null
  })),

  // Update Program
  on(ProgramActions.updateProgram, (state) => ({
    ...state,
    loading: true,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(ProgramActions.updateProgramSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    program: null,
    saveSuccess: true,
  })),
  on(ProgramActions.updateAiProgram, (state) => ({
    ...state,
    loading: true,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(ProgramActions.updateAiProgramSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    program: null,
    saveSuccess: true,
  })),


  on(ProgramActions.updateProgramFailure, (state, { error }) => ({
    ...state,
    loading: false,
    program: null,
    error,
    saveFailure: true,
  })),

  // Delete Program
  on(ProgramActions.deleteProgram, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ProgramActions.deleteProgramSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(ProgramActions.deleteProgramFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(ProgramActions.setProgramQueryParams, (state, { params, body }) => ({
    ...state,
    page: params.page ?? state.page,
    limit: params.limit ?? state.limit,
    filter: body?.filter ?? state.filter,
    sort: body?.sort ?? state.sort,
  })),
  on(ProgramActions.clearSingleProgram, (state) => ({
    ...state,
    program:null
  })),
  on(ProgramActions.clearProgramNames, (state) => ({
    ...state,
    programNames:[]
  })),
  on(AuthActions.logout, () => initialProgramState)
);
